import { Dialog, DialogTitle, DialogContent, Grid, Stack, Box, Typography, Button, Divider, Checkbox, Tooltip, IconButton, Link, useTheme } from '@mui/material'
import { Cancel, CloudUpload, Launch } from '@mui/icons-material';
import { Menu, MenuItem, ListItemIcon, CircularProgress } from '@mui/material';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningIcon from '@mui/icons-material/Warning';
import FlagIcon from '@mui/icons-material/Flag';
import Barcode_Mini_Icon from '../../../Assets/Barcode_Mini_Icon.png';
import Barcode_Mini_Icon_White from '../../../Assets/Barcode_Mini_Icon_White.png';
import ScanImageCarousel from './CenterControlComponents/scanPictureComponents/ScanImageCarousel';
import ErrorDetails from './CenterControlComponents/ErrorDetails';
import ReportScanDialog from './CenterControlComponents/ReportScanDialog';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import _ from 'lodash';
import { getShortUnitString } from '../../../ts/Common';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Cargo } from '../../../ts/Cargo';
import { useFeatureSupported } from '../../../FeatureFlagsContext';
import FilePaths from './CenterControlComponents/FilePaths';

interface ScanDetailsProps {
    openScanDetails: boolean;
    setOpenScanDetails: (open: boolean) => void;
    cargoResponse: Cargo;
    imagePaths: string[];
    cloudPaths: string[];
    xmlPaths: string[];
    pdfPaths: string[];
    rawImagePaths: string[];
    rawCloudPaths: string[];
    rawXmlPaths: string[];
    rawPdfPaths: string[];
    setShowAuditDims: (show: boolean) => void;
    selectedScanIndex: number;
    setShowCertification: (show: boolean) => void;
    setFlashCertificationGrid: (flash: boolean) => void;
    handleReupload?: (cargoResponse: Cargo) => Promise<void>;
    handleRexport?: (cargoResponse: Cargo) => Promise<void>;
    handleReportScan?: (cargoResponse: Cargo, dims: { Length: number; Width: number; Height: number }) => Promise<void>;
}

export default function ScanDetails(props: ScanDetailsProps) {
    const { openScanDetails, setOpenScanDetails, cargoResponse, imagePaths, cloudPaths, xmlPaths, pdfPaths, rawImagePaths, rawCloudPaths, rawXmlPaths, rawPdfPaths, setShowAuditDims, selectedScanIndex, setShowCertification, setFlashCertificationGrid, handleReupload, handleRexport, handleReportScan } = props;
    const [reuploadLoading, setReuploadLoading] = useState(false);
    const [rexportLoading, setRexportLoading] = useState(false);
    const [reportScanOpen, setReportScanOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);
    const recallSupported = useFeatureSupported ? useFeatureSupported('Recall') : false;
    const theme = useTheme();
    // Menu handlers
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleReuploadClick = async () => {
        if (!cargoResponse || !handleReupload) return;
        setReuploadLoading(true);
        try {
            await handleReupload(cargoResponse);
        } finally {
            setReuploadLoading(false);
        }
    };
    const handleRexportClick = async () => {
        if (!cargoResponse || !handleRexport) return;
        setRexportLoading(true);
        try {
            await handleRexport(cargoResponse);
        } finally {
            setRexportLoading(false);
        }
    };
    const { t } = useTranslation();
    const [certifiedDimension, setCertifiedDimensions] = useState<any[]>([true, true, true]);

    useEffect(() => {
        if (cargoResponse) {
            const dimensionBounds = cargoResponse.dimensions.dimensionBounds;
            setCertifiedDimensions([dimensionBounds.lengthCertified, dimensionBounds.widthCertified, dimensionBounds.heightCertified]);
        } else {
            setCertifiedDimensions([true, true, true]);
        }
    }, [selectedScanIndex, cargoResponse]);

    const styles = {
        menuPaper: {
            boxShadow: '0px 4px 16px rgba(0,0,0,0.15)',
            borderRadius: 2,
            minWidth: 180,
            p: 0.5,
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            px: 2,
        },
        barcode: {
            fontWeight: 450,
            color: theme.palette.mode === 'dark' ? theme.palette.spectre.textPrimary : 'var(--spectre-text-muted)',
            display: 'inline',
        },
        barcodeImage: {
            display: "inline",
            pr: 1,
            pl: 0.5
        },
        timeSinceText: {
            fontWeight: 550,
            color: theme.palette.mode === 'dark' ? theme.palette.spectre.textPrimary : 'var(--spectre-text-muted)',
            align: 'center'
        },
        divider: {
            backgroundColor: 'var(--spectre-navigation)',
            mt: 2,
            mb: 2
        },
        length: {
            fontWeight: 750,
            color: theme.palette.mode === 'dark' ? '#4ADE80' : '#369350',
            display: 'inline'
        },
        width: {
            fontWeight: 750,
            color: theme.palette.mode === 'dark' ? '#7C83FF' : '#1627BA',
            display: 'inline'
        },
        height: {
            fontWeight: 750,
            color: theme.palette.mode === 'dark' ? '#FF6B6B' : '#D43636',
            display: 'inline'
        },
        net: {
            fontWeight: 750,
            color: '#E07F3B',
            display: 'inline'
        },
        dimText: {
            fontWeight: 750,
            display: 'inline'
        },
        outOfBoundsBox: {
            backgroundColor: theme.palette.mode === 'dark' ? 'var(--spectre-warning-surface)' : 'var(--spectre-warning-accent)',
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            boxSizing: 'border-box',
            border: theme.palette.mode === 'dark' ? '1px solid var(--spectre-warning-border)' : 'none',
        },
        uploadExportButtons: {
            color: "#FFFFFF",
            backgroundColor: 'var(--spectre-navigation)',
            '&:hover': {
                backgroundColor: 'var(--spectre-navigation-hover)',
                borderColor: 'var(--spectre-border)'
            },
            '&:disabled': {
                color: "#FFFFFF",
                backgroundColor: 'var(--spectre-surface-muted)'
            }
        },
        successCheck: {
            fontWeight: 600,
            color: '#1d9223ff',
            display: 'inline',
            position: 'relative',
            top: '4px'
        },
        failedX: {
            fontWeight: 600,
            color: '#FF0000',
            display: 'inline',
            position: 'relative',
            top: '4px'
        },
        nullBox: {
            fontWeight: 600,
            display: 'inline'
        },
        reportButton: {
            mr: 1,
            border: '1px solid var(--spectre-border)',
            '&: hover': {
                borderColor: '#000000'
            },
        }
    };

    const successCheck = (
        <Box component="span" sx={styles.successCheck}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#1d9223ff" />
                <path d="M6 10.5L9 13.5L14 8.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    );

    const failureCheck = (
        <Box component="span" sx={styles.failedX}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#c23737" />
                <path d="M7 7 L13 13 M13 7 L7 13" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    );

    // place holder for scan failures
    const scanFailure = false;

    // Check if any dimension is out of certified range
    const hasOutOfRangeDimensions = certifiedDimension.some(cert => !cert) || cargoResponse?.dimensions?.dimensionBounds.overallCertified === false;

    const openCertificationModal = () => {
        setFlashCertificationGrid?.(true);
        setShowCertification?.(true);
    }

    const warningIcon = (
        <Tooltip title={t('control.centerControl.dimensions.outOfRangeShort')}>
            <WarningIcon
                sx={{
                    color: 'var(--spectre-warning-accent)',
                    fontSize: '18px',
                    ml: 0.5
                }}
            />
        </Tooltip>
    );

    return (
        <>
        <Dialog
            open={openScanDetails}
            onClose={() => { setOpenScanDetails(false) }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            sx={{
                '& .MuiDialog-paper': {
                    width: { md: '80%', sm: '95%', xs: '95%' },
                    maxHeight: '95%',
                    borderRadius: '16px',
                    border: '2px solid #000'
                }
            }}
        >
            {cargoResponse ? (
                <>
                    <DialogTitle sx={{ position: 'relative', pr: 6 }}>
                        <Grid container alignItems="center">
                            <Grid item md={4} xs={4} style={{ textAlign: 'left' }}>
                            </Grid>
                            <Grid item md={4} xs={4} style={{ textAlign: 'center' }}>
                                <Typography component={'span'} sx={styles.timeSinceText}>{cargoResponse.timeStamp.toLocaleTimeString()}</Typography>
                            </Grid>
                            <Grid item md={4} xs={4} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                {/* Dot-dot-dot menu */}
                                <IconButton
                                    aria-label="more options"
                                    onClick={handleMenuOpen}
                                    sx={{ ml: 1 }}
                                >
                                    <MoreVertIcon sx={{ fontSize: 24, color: theme.palette.mode === 'dark' ? theme.palette.spectre.textPrimary : 'var(--spectre-text-muted)' }} />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={openMenu}
                                    onClose={handleMenuClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    PaperProps={{ sx: styles.menuPaper }}
                                    MenuListProps={{ sx: { p: 0 } }}
                                >
                                    <MenuItem
                                        sx={styles.menuItem}
                                        component="a"
                                        href={`https://spectre-licensing.com/Cargo/Details/${cargoResponse ? cargoResponse.cargoID : ""}`}
                                        target="_blank"
                                        onClick={handleMenuClose}
                                        disabled={!(cargoResponse && cargoResponse.cargoID)}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <Launch sx={{ fontSize: 18 }} />
                                        </ListItemIcon>
                                        <span>View Details</span>
                                    </MenuItem>
                                    {recallSupported && [
                                        <MenuItem
                                            key="reupload"
                                            sx={styles.menuItem}
                                            onClick={async () => { await handleReuploadClick(); handleMenuClose(); }}
                                            disabled={!cargoResponse || reuploadLoading || cargoResponse.uploaded === true}
                                        >
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {reuploadLoading ? <CircularProgress size={18} /> : <CloudUpload sx={{ fontSize: 18 }} />}
                                            </ListItemIcon>
                                            <span>{t('control.centerControl.centerBarcodeFieldButtons.reupload') || 'Re-upload'}</span>
                                        </MenuItem>,
                                        <MenuItem
                                            key="rexport"
                                            sx={styles.menuItem}
                                            onClick={async () => { await handleRexportClick(); handleMenuClose(); }}
                                            disabled={rexportLoading}
                                        >
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {rexportLoading ? <CircularProgress size={18} /> : <DriveFileMoveIcon sx={{ fontSize: 18 }} />}
                                            </ListItemIcon>
                                            <span>{t('control.centerControl.centerBarcodeFieldButtons.reexport') || 'Re-export'}</span>
                                        </MenuItem>,
                                        <MenuItem
                                            key="reportScan"
                                            sx={styles.menuItem}
                                            onClick={() => { handleMenuClose(); setReportScanOpen(true); }}
                                            disabled={!cargoResponse?.cargoFolder}
                                        >
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <FlagIcon sx={{ fontSize: 18 }} />
                                            </ListItemIcon>
                                            <span>{t('control.centerControl.centerBarcodeFieldButtons.reportScan') || 'Report Scan'}</span>
                                        </MenuItem>
                                    ]}
                                    {!recallSupported && (
                                        <MenuItem
                                            sx={styles.menuItem}
                                            onClick={() => { setShowAuditDims(true); handleMenuClose(); }}
                                            disabled={selectedScanIndex === 0 && cargoResponse ? false : true}
                                        >
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <FlagIcon sx={{ fontSize: 18 }} />
                                            </ListItemIcon>
                                            <span>{t('control.centerControl.centerBarcodeFieldButtons.reportScan') || 'Report Scan'}</span>
                                        </MenuItem>
                                    )}
                                </Menu>
                                <IconButton
                                    aria-label="close"
                                    onClick={() => { setOpenScanDetails(false) }}
                                    sx={(theme) => ({
                                        color: theme.palette.grey[500],
                                    })}
                                >
                                    <CloseIcon sx={{ fontSize: 24, color: theme.palette.mode === 'dark' ? theme.palette.spectre.textPrimary : 'var(--spectre-text-muted)' }} />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </DialogTitle>
                    <DialogContent sx={{ pb: 0 }}>
                        <Stack justifyContent={"space-around"} spacing={1} divider={<Divider orientation='horizontal'></Divider>}>
                            {/* ...existing code... (all content except Uploaded/Exported section) */}
                            <Grid container>
                                <Grid item md={12} xs={12} style={{ textAlign: 'left' }} sx={{ pt: 1 }} >
                                    <Typography component={'span'} noWrap sx={styles.barcode}>
                                        {scanFailure ? '' : cargoResponse.barcode !== '' ?
                                            <Box
                                                component="img"
                                                sx={styles.barcodeImage}
                                                alt="Barcode:"
                                                src={theme.palette.mode === 'dark' ? Barcode_Mini_Icon_White : Barcode_Mini_Icon}
                                            /> : <Box />
                                        }
                                        {scanFailure ? '' : cargoResponse.barcode}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <ErrorDetails cargoResponse={cargoResponse} handleReuploadClick={handleReuploadClick} reuploadLoading={reuploadLoading} handleRexportClick={handleRexportClick} rexportLoading={rexportLoading}></ErrorDetails>
                                </Grid>
                            </Grid>
                            <Grid container style={{ textAlign: 'center' }}>
                                <Grid container sx={{ pb: 1 }} direction={'row'} justifyContent={'space-between'} >
                                    {hasOutOfRangeDimensions && (
                                        <Grid container sx={{ pb: 1 }} justifyContent="center">
                                            <Grid item xs={10} md={8}>
                                                <Box sx={styles.outOfBoundsBox}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#333',
                                                            fontWeight: 500,
                                                            fontSize: { xs: '.875rem', md: '1rem' },
                                                            textAlign: 'center',
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <Trans
                                                            i18nKey="control.centerControl.dimensions.outOfRangeWithLink"
                                                            components={[
                                                                <Link
                                                                    component="button"
                                                                    type="button"
                                                                    underline="always"
                                                                    onClick={openCertificationModal}
                                                                    sx={{
                                                                        color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                                        textDecorationColor: theme.palette.mode === 'dark' ? '#FFFFFF' : undefined,
                                                                        fontWeight: 700,
                                                                        cursor: 'pointer',
                                                                        p: 0,
                                                                        minWidth: 0,
                                                                        verticalAlign: 'baseline'
                                                                    }}
                                                                />
                                                            ]}
                                                        />
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    )}
                                    <Grid item xs={4}>
                                        <Typography component={'span'} sx={styles.length}>{t('control.eventHistory.scanDetails.length')}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography component={'span'} sx={styles.width}>{t('control.eventHistory.scanDetails.width')}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography component={'span'} sx={styles.height}>{t('control.eventHistory.scanDetails.height')}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <Typography component={'span'} sx={styles.dimText}>{cargoResponse.dimensions.length}  {getShortUnitString(cargoResponse.units.length)}</Typography>
                                            {!certifiedDimension[0] && warningIcon}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <Typography component={'span'} sx={styles.dimText}>{cargoResponse.dimensions.width}  {getShortUnitString(cargoResponse.units.length)}</Typography>
                                            {!certifiedDimension[1] && warningIcon}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <Typography component={'span'} sx={styles.dimText}>{cargoResponse.dimensions.height}  {getShortUnitString(cargoResponse.units.length)}</Typography>
                                            {!certifiedDimension[2] && warningIcon}
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Grid container direction={'row'} justifyContent={'space-between'}>
                                    <Grid container>
                                        <Grid item xs>
                                            <Typography component={'span'} sx={styles.net}>{t('control.eventHistory.scanDetails.volume')}</Typography>
                                        </Grid>
                                        <Grid item xs>
                                            <Typography component={'span'} sx={styles.net}>{t('control.eventHistory.scanDetails.weight')}</Typography>
                                        </Grid>
                                        <Grid item xs>
                                            <Typography component={'span'} sx={styles.net}>{t('control.eventHistory.scanDetails.density')}</Typography>
                                        </Grid>
                                        {cargoResponse?.dimensions.freightClass?.class && (
                                            <Grid item xs>
                                                <Typography component={'span'} sx={styles.net}>{t('control.eventHistory.scanDetails.class')}</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs>
                                            <Typography component={'span'} sx={styles.dimText}>{cargoResponse.dimensions.volume} {getShortUnitString(cargoResponse.units.volume)}</Typography>
                                        </Grid>
                                        <Grid item xs>
                                            <Typography component={'span'} sx={styles.dimText}>{cargoResponse.dimensions.weight.display} {cargoResponse.units.weight}</Typography>
                                        </Grid>
                                        <Grid item xs>
                                            <Typography component={'span'} sx={styles.dimText}>{cargoResponse.dimensions.density} {getShortUnitString(cargoResponse.units.density)}</Typography>
                                        </Grid>
                                        {cargoResponse?.dimensions.freightClass?.class && (
                                            <Grid item xs>
                                                <Typography component={'span'} sx={styles.dimText}>
                                                    {cargoResponse.dimensions.freightClass?.class}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={12}>
                                    <ScanImageCarousel imagePaths={imagePaths} height={'100%'} width={'100%'}></ScanImageCarousel>
                                </Grid>
                            </Grid>
                            {(_.has(cargoResponse, 'customFields.CustomFields.Field') && Array.isArray(cargoResponse.customFields.CustomFields.Field)) ? (
                                <Grid container>
                                    <TableContainer component={Paper}>
                                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                            <TableHead>
                                                <TableRow>
                                                    {cargoResponse.customFields.CustomFields.Field.map((field: any) => (
                                                        <TableCell align="center" key={field.Name}>{field.Name}</TableCell>
                                                    ))}
                                                    {cargoResponse.dimensions.freightClass?.class && cargoResponse.dimensions.freightClass?.class !== '' && (
                                                        <>
                                                            <TableCell align="center">{t('control.eventHistory.scanDetails.freightClass')}</TableCell>
                                                            <TableCell align="center">{t('control.eventHistory.scanDetails.itemNumber')}</TableCell>
                                                            <TableCell align="center">{t('control.eventHistory.scanDetails.subItemNumber')}</TableCell>
                                                        </>
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    {cargoResponse.customFields.CustomFields.Field.map((field: any) => {
                                                        if (field.Value === true || field.Value === false) {
                                                            return <TableCell align="center" key={field.Name}><Checkbox checked={field.Value} /></TableCell>
                                                        }
                                                        return <TableCell align="center" key={field.Name}>{field.Value}</TableCell>
                                                    })}
                                                    {cargoResponse.dimensions.freightClass?.class && cargoResponse.dimensions.freightClass?.class !== '' && (
                                                        <>
                                                            <TableCell align="center">{cargoResponse.dimensions.freightClass?.class}</TableCell>
                                                            <TableCell align="center">{cargoResponse.dimensions.freightClass?.itemNumber}</TableCell>
                                                            <TableCell align="center">{cargoResponse.dimensions.freightClass?.subItemNumber}</TableCell>
                                                        </>
                                                    )}
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            ) : ''}
                            <Box sx={{ pb: 1.5 }}>
                                <FilePaths
                                    fullWidth
                                    imagePaths={imagePaths}
                                    cloudPaths={cloudPaths}
                                    xmlPaths={xmlPaths}
                                    pdfPaths={pdfPaths}
                                    rawImagePaths={rawImagePaths}
                                    rawCloudPaths={rawCloudPaths}
                                    rawXmlPaths={rawXmlPaths}
                                    rawPdfPaths={rawPdfPaths}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>
                    {/* Sticky Uploaded/Exported section at the bottom */}
                    <Box sx={{
                        position: 'sticky',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        background: theme.palette.mode === 'dark' ? 'var(--spectre-surface)' : '#f5f5f5',
                        borderTop: theme.palette.mode === 'dark'
                            ? '1px solid var(--spectre-border)'
                            : '1px solid #e0e0e0',
                        zIndex: 1201,
                        py: 1,
                        px: 2,
                    }}>
                        <Grid container style={{ textAlign: 'center' }}>
                            <Grid item md={6} xs={6} display="flex" alignItems="center" justifyContent="center">
                                <Typography
                                    component={'span'}
                                    sx={{ ...styles.barcode, color: theme.palette.mode === 'dark' ? '#FFFFFF' : undefined }}
                                    ml={1}
                                >
                                    {t('control.eventHistory.scanDetails.uploaded')}: {cargoResponse.uploaded == null
                                        ? <Typography sx={styles.nullBox} component="span">&#9744;</Typography>
                                        : cargoResponse.uploaded
                                            ? successCheck
                                            : failureCheck}
                                </Typography>
                            </Grid>
                            <Grid item md={6} xs={6} display="flex" alignItems="center" justifyContent="center">
                                <Typography
                                    component={'span'}
                                    sx={{ ...styles.barcode, color: theme.palette.mode === 'dark' ? '#FFFFFF' : undefined }}
                                    ml={1}
                                >
                                    {t('control.eventHistory.scanDetails.exported')}: {cargoResponse.exported == null
                                        ? <Typography sx={styles.nullBox} component="span">&#9744;</Typography>
                                        : cargoResponse.exported
                                            ? successCheck
                                            : failureCheck}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </>
            ) : (
                <>
                    <DialogTitle>
                        <Typography>{t('control.eventHistory.scanDetails.noCargoSelected')}</Typography>
                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Button className="Blue-Button" onClick={() => { setOpenScanDetails(false) }} startIcon={<Cancel />}>
                                <Typography sx={{ fontWeight: 550, fontSize: 15 }}>{t('control.centerControl.closeButton')}</Typography>
                            </Button>
                        </Box>
                    </DialogTitle>
                </>
            )}
        </Dialog>
        <ReportScanDialog
            open={reportScanOpen}
            onClose={() => setReportScanOpen(false)}
            cargoResponse={cargoResponse}
            handleReportScan={handleReportScan!}
        />
        </>
    )
}